import { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { getRaces } from '../api/races'
import { getEvents } from '../api/events'
import i18n from '../i18n'
import { localizeContent } from '../i18n/localizeContent'

const byDateDescending = (a, b) =>
  new Date(b.date?.[0]).getTime() - new Date(a.date?.[0]).getTime()

const DataContext = createContext(null)

export function DataProvider ({ children }) {
  const { pathname } = useLocation()
  const isAdminRoute = pathname.startsWith('/admin')
  const [rawRaces, setRawRaces] = useState([])
  const [rawEvents, setRawEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [lang, setLang] = useState(i18n.resolvedLanguage || i18n.language || 'en')

  // Re-localize already-loaded documents when the user switches language.
  useEffect(() => {
    const onChange = lng => setLang(lng)
    i18n.on('languageChanged', onChange)
    return () => i18n.off('languageChanged', onChange)
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [racesData, eventsData] = await Promise.all([getRaces(), getEvents()])
      setRawRaces([...(racesData || [])])
      setRawEvents([...(eventsData || [])])
    } catch (err) {
      setError(err.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }, [])

  // Load races/events only when a public page is mounted (skip admin routes).
  // Re-loads when navigating from /admin back to the public site.
  useEffect(() => {
    if (!isAdminRoute) load()
  }, [isAdminRoute, load])

  const races = useMemo(
    () => rawRaces.map(doc => localizeContent(doc, lang)).sort(byDateDescending),
    [rawRaces, lang]
  )
  const events = useMemo(
    () => rawEvents.map(doc => localizeContent(doc, lang)).sort(byDateDescending),
    [rawEvents, lang]
  )

  const value = useMemo(() => ({
    races,
    events,
    loading,
    error,
    refresh: load
  }), [races, events, loading, error, load])

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export function useData () {
  return useContext(DataContext)
}

export function useRaces () {
  return useContext(DataContext).races
}

export function useEvents () {
  return useContext(DataContext).events
}

export function useEvent (slug) {
  const { events, loading } = useData()
  const event = useMemo(() => events.find(e => e.slug === slug), [events, slug])
  return { event, loading }
}
